import { deleteWorkflowTemplateVersion } from '@commenda/api';
import { IWorkflowTemplateVersion } from '@commenda/types';
import { WorkflowTemplateVersionStatus } from '@prisma/client';
import { useCallback, useState } from 'react';
import { WorkflowBuilderChatHelper } from './helpers/builder-chat';
import { TBuilderConfirmation } from './types/types';

/** Discarding the draft the chat forked. Publishing and going live belong to the editor toolbar's
 * publish popover, which every workflow editor shows. */
export function useBuilderChatVersionActions(
  params: Readonly<{
    version: IWorkflowTemplateVersion;
    onNavigateToVersion: (workflowTemplateVersionId: string) => void;
  }>
) {
  const { version, onNavigateToVersion } = params;

  const [error, setError] = useState<string | null>(null);
  const [pendingConfirmation, setPendingConfirmation] =
    useState<TBuilderConfirmation | null>(null);

  const discard = useCallback(async () => {
    const result = await deleteWorkflowTemplateVersion(version.id);
    if (!result.success) {
      setError(result.error ?? 'Could not discard this draft.');
      return;
    }

    // Leaving the author on a deleted version would leave the conversation bound to a row that no longer
    // exists, and every later write would be refused with no way to rebind.
    if (version.starterWorkflowTemplateVersionId) {
      onNavigateToVersion(version.starterWorkflowTemplateVersionId);
    }
  }, [
    version.id,
    version.starterWorkflowTemplateVersionId,
    onNavigateToVersion,
  ]);

  const requestDiscard = useCallback(
    () =>
      setPendingConfirmation(
        WorkflowBuilderChatHelper.getDiscardConfirmation(version, discard)
      ),
    [version, discard]
  );

  const dismissConfirmation = useCallback(
    () => setPendingConfirmation(null),
    []
  );

  return {
    error,
    canDiscard:
      version.status === WorkflowTemplateVersionStatus.DRAFT &&
      Boolean(version.starterWorkflowTemplateVersionId),
    pendingConfirmation,
    requestDiscard,
    dismissConfirmation,
  };
}
